import { useEffect, useState } from "react";
import { Users, Send, Loader2, AlertTriangle, PackageX, DollarSign, RefreshCw, Calendar, Clock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  adminListSegments, adminPreviewSegment, adminSendBlast,
  adminLowStock, adminSendLowStockDigest,
  adminMarginSummary,
  adminScheduleBlast, adminListBlasts, adminCancelBlast,
} from "../../lib/api";

/**
 * Marketing + Ops admin tab. Three sections stacked:
 *
 *   1. Customer Segments — pick one, preview top recipients, fire a
 *      branded email blast (or test send to NOTIFICATION_EMAIL).
 *   2. Low-Stock Alerts — ready-made products under a threshold, with
 *      one-click "Email me the digest" button.
 *   3. Profit Margins — revenue / cost / profit / margin% rollup for the
 *      trailing N days, plus a count of products missing `cost_per_unit`.
 *
 * Keeping these together (rather than 3 separate tabs) keeps the admin
 * sidebar manageable while still surfacing each capability prominently.
 */
export default function MarketingTab({ pw }) {
  return (
    <div className="space-y-12" data-testid="marketing-tab">
      <SegmentsPanel pw={pw} />
      <ScheduledBlastsPanel pw={pw} />
      <LowStockPanel pw={pw} />
      <MarginsPanel pw={pw} />
    </div>
  );
}

// ============ SEGMENTS ============

function SegmentsPanel({ pw }) {
  const [segments, setSegments] = useState([]);
  const [active, setActive] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [scheduledFor, setScheduledFor] = useState("");
  const [sending, setSending] = useState(false);

  const reload = () => {
    setLoading(true);
    adminListSegments(pw)
      .then((r) => setSegments(r.segments || []))
      .catch(() => toast.error("Failed to load segments"))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [pw]);  // eslint-disable-line react-hooks/exhaustive-deps

  const pickSegment = async (key) => {
    setActive(key);
    setComposing(false);
    setPreview(null);
    try {
      const p = await adminPreviewSegment(key, pw, 10);
      setPreview(p);
    } catch {
      toast.error("Preview failed");
    }
  };

  const sendBlast = async (testOnly) => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body required"); return; }
    if (!testOnly && !window.confirm(`Send this email to ${preview?.count || 0} recipients?`)) return;
    setSending(true);
    try {
      const res = await adminSendBlast({
        segment_key: active, subject, body_text: body,
        cta_label: ctaLabel || null, cta_url: ctaUrl || null,
        test_only: testOnly,
      }, pw);
      toast.success(testOnly ? `Test sent to ${res.to}` : `Blast sent to ${res.sent} / ${res.intended}`);
      if (!testOnly) {
        setComposing(false); setSubject(""); setBody(""); setCtaLabel(""); setCtaUrl(""); setScheduledFor("");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  /**
   * Queue the blast to fire later. Uses the same compose form but routes
   * through `/admin/blasts/schedule` so the minute-cron picks it up at
   * the configured wallclock time.
   */
  const scheduleBlast = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body required"); return; }
    if (!scheduledFor) { toast.error("Pick a date and time to schedule"); return; }
    // Convert local-input datetime to ISO with timezone for the backend.
    const iso = new Date(scheduledFor).toISOString();
    setSending(true);
    try {
      await adminScheduleBlast({
        segment_key: active, subject, body_text: body,
        cta_label: ctaLabel || null, cta_url: ctaUrl || null,
        scheduled_for: iso,
      }, pw);
      toast.success(`Scheduled for ${new Date(scheduledFor).toLocaleString()}`);
      setComposing(false); setSubject(""); setBody(""); setCtaLabel(""); setCtaUrl(""); setScheduledFor("");
      // Trigger the queue panel to refresh.
      window.dispatchEvent(new CustomEvent("tc-blast-scheduled"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Schedule failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="segments-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Users size={20} className="text-[var(--tc-red)]" />
          <h2 className="font-display text-3xl uppercase">Customer Segments</h2>
        </div>
        <button onClick={reload} className="p-2 border border-[#222] hover:border-[var(--tc-red)] hover:text-[var(--tc-red)]" aria-label="Refresh segments" data-testid="segments-refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {loading ? (
        <div className="py-8 text-center text-zinc-500 font-mono text-xs">LOADING…</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {segments.map((s) => (
            <button
              key={s.key}
              data-testid={`segment-${s.key}`}
              onClick={() => pickSegment(s.key)}
              className={`text-left p-4 border transition-colors ${
                active === s.key ? "border-[var(--tc-red)] bg-[#141414]" : "border-[#222] hover:border-zinc-600"
              }`}
            >
              <div className="font-ui font-bold uppercase tracking-wider text-sm">{s.label}</div>
              <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-1">{s.count} CUSTOMER{s.count === 1 ? "" : "S"}</div>
              <p className="text-xs text-zinc-400 mt-2 line-clamp-2">{s.description}</p>
            </button>
          ))}
        </div>
      )}

      {active && preview && (
        <div className="mt-6 border border-[#222] p-5" data-testid="segment-preview">
          <div className="flex justify-between items-center mb-3">
            <div className="font-mono text-[10px] tracking-widest text-zinc-500">
              PREVIEW · {preview.count} TOTAL · SHOWING TOP {preview.people.length}
            </div>
            <button
              data-testid="segment-compose-btn"
              onClick={() => setComposing((v) => !v)}
              disabled={preview.count === 0}
              className="px-3 py-2 border border-[var(--tc-red)] text-[var(--tc-red)] hover:bg-[var(--tc-red)] hover:text-white font-mono text-[10px] tracking-widest disabled:opacity-50 flex items-center gap-2"
            >
              <Send size={12} /> {composing ? "CANCEL" : "COMPOSE BLAST"}
            </button>
          </div>
          {preview.count === 0 ? (
            <div className="py-4 text-center text-zinc-500 text-xs">No matching customers yet.</div>
          ) : (
            <div className="max-h-64 overflow-y-auto text-xs font-mono">
              {preview.people.map((p) => (
                <div key={p.email} className="py-1.5 flex justify-between gap-3 border-b border-[#1a1a1a]">
                  <span className="truncate">{p.name || "—"} · <span className="text-zinc-500">{p.email}</span></span>
                  <span className="text-zinc-500 whitespace-nowrap">${(p.total_spent || 0).toFixed(0)} · {p.order_count}x</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {composing && (
        <div className="mt-4 border border-[var(--tc-red)] bg-[#0a0a0a] p-5 space-y-3" data-testid="blast-compose">
          <input
            value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={140}
            placeholder="SUBJECT LINE"
            data-testid="blast-subject"
            className="w-full bg-[#0d0d0d] border border-[#222] px-3 py-2 font-mono text-xs tracking-widest outline-none focus:border-[var(--tc-red)]"
          />
          <textarea
            value={body} onChange={(e) => setBody(e.target.value)} maxLength={8000} rows={6}
            placeholder="Email body — separate paragraphs with a blank line. Plain text only; line breaks preserved."
            data-testid="blast-body"
            className="w-full bg-[#0d0d0d] border border-[#222] px-3 py-2 font-body text-sm outline-none focus:border-[var(--tc-red)] resize-y"
          />
          <div className="grid sm:grid-cols-2 gap-3">
            <input
              value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} maxLength={40}
              placeholder="CTA LABEL · OPTIONAL"
              data-testid="blast-cta-label"
              className="bg-[#0d0d0d] border border-[#222] px-3 py-2 font-mono text-xs tracking-widest outline-none focus:border-[var(--tc-red)]"
            />
            <input
              value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} maxLength={300}
              placeholder="CTA URL · OPTIONAL"
              data-testid="blast-cta-url"
              className="bg-[#0d0d0d] border border-[#222] px-3 py-2 font-mono text-xs tracking-widest outline-none focus:border-[var(--tc-red)]"
            />
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => sendBlast(true)}
              disabled={sending}
              data-testid="blast-test-btn"
              className="px-4 py-2 border border-[#222] hover:border-white font-mono text-[10px] tracking-widest flex items-center gap-2 disabled:opacity-50"
            >
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} TEST SEND
            </button>
            <button
              onClick={() => sendBlast(false)}
              disabled={sending || !preview?.count}
              data-testid="blast-send-btn"
              className="flex-1 px-4 py-2 border border-[var(--tc-red)] bg-[var(--tc-red)] text-white hover:bg-[var(--tc-red)]/80 font-mono text-[10px] tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} SEND NOW TO {preview?.count || 0}
            </button>
          </div>

          {/* Schedule-for-later row */}
          <div className="pt-3 mt-3 border-t border-[#1a1a1a] flex flex-col sm:flex-row gap-2">
            <label className="flex-1 flex items-center gap-2 bg-[#0d0d0d] border border-[#222] px-3">
              <Calendar size={12} className="text-zinc-500 flex-shrink-0" />
              <input
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
                data-testid="blast-schedule-time"
                className="flex-1 bg-transparent py-2 font-mono text-xs outline-none"
              />
            </label>
            <button
              onClick={scheduleBlast}
              disabled={sending || !scheduledFor || !preview?.count}
              data-testid="blast-schedule-btn"
              className="px-4 py-2 border border-[var(--tc-blue,#3b82f6)] text-[var(--tc-blue,#3b82f6)] hover:bg-[var(--tc-blue,#3b82f6)] hover:text-white font-mono text-[10px] tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Clock size={12} /> SCHEDULE
            </button>
          </div>
          <p className="font-mono text-[9px] tracking-widest text-zinc-600">
            UNSUBSCRIBED CUSTOMERS WILL BE SKIPPED · CAPPED AT 500 RECIPIENTS PER BLAST
          </p>
        </div>
      )}
    </section>
  );
}

// ============ LOW STOCK ============

function LowStockPanel({ pw }) {
  const [threshold, setThreshold] = useState(10);
  const [data, setData] = useState({ items: [], count: 0 });
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setLoading(true);
    adminLowStock(pw, threshold)
      .then(setData)
      .catch(() => toast.error("Low-stock query failed"))
      .finally(() => setLoading(false));
  }, [pw, threshold]);

  const sendDigest = async () => {
    if (data.count === 0) { toast("No items below threshold"); return; }
    setSending(true);
    try {
      const r = await adminSendLowStockDigest(pw, threshold);
      if (r.sent) toast.success(`Digest sent to ${r.to}`);
      else toast.warning(r.reason || "Email backend not configured");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <section data-testid="low-stock-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <PackageX size={20} className="text-[var(--tc-red)]" />
          <h2 className="font-display text-3xl uppercase">Low Stock</h2>
          {data.count > 0 && (
            <span className="font-mono text-[10px] tracking-widest text-[var(--tc-red)] flex items-center gap-1">
              <AlertTriangle size={12} /> {data.count} ITEMS
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="font-mono text-[10px] tracking-widest text-zinc-500">THRESHOLD</label>
          <input
            type="number" min={0} max={1000}
            value={threshold}
            onChange={(e) => setThreshold(Math.max(0, Math.min(1000, Number(e.target.value) || 0)))}
            className="w-20 bg-[#0d0d0d] border border-[#222] px-2 py-1 font-mono text-xs outline-none focus:border-[var(--tc-red)]"
            data-testid="low-stock-threshold"
          />
          <button
            onClick={sendDigest}
            disabled={sending || data.count === 0}
            data-testid="low-stock-send-btn"
            className="px-3 py-2 border border-[var(--tc-red)] text-[var(--tc-red)] hover:bg-[var(--tc-red)] hover:text-white font-mono text-[10px] tracking-widest disabled:opacity-50 flex items-center gap-2"
          >
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} EMAIL DIGEST
          </button>
        </div>
      </div>
      {loading ? (
        <div className="py-6 text-center text-zinc-500 font-mono text-xs">LOADING…</div>
      ) : data.count === 0 ? (
        <div className="py-6 border border-[#222] text-center text-zinc-500 font-mono text-xs">
          ALL READY-MADE ITEMS ABOVE {threshold} IN STOCK 🎉
        </div>
      ) : (
        <div className="border border-[#222] divide-y divide-[#1a1a1a]">
          {data.items.map((it) => (
            <div key={it.id} data-testid={`low-stock-row-${it.id}`} className="p-3 flex items-center gap-3">
              {it.image_front && (
                <img src={it.image_front} alt="" className="w-12 h-12 object-cover bg-black border border-[#222]" />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-ui font-bold uppercase tracking-wider text-sm truncate">{it.name}</div>
                <div className="font-mono text-[10px] text-zinc-500">{it.category}</div>
              </div>
              <div className="text-right">
                <div className="font-display text-2xl text-[var(--tc-red)]">{it.stock}</div>
                <div className="font-mono text-[9px] tracking-widest text-zinc-500">LEFT</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ============ MARGINS ============

function MarginsPanel({ pw }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showOrders, setShowOrders] = useState(false);

  useEffect(() => {
    setLoading(true);
    adminMarginSummary(pw, days, showOrders)
      .then(setData)
      .catch(() => toast.error("Margins query failed"))
      .finally(() => setLoading(false));
  }, [pw, days, showOrders]);

  return (
    <section data-testid="margins-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <DollarSign size={20} className="text-[var(--tc-red)]" />
          <h2 className="font-display text-3xl uppercase">Profit Margins</h2>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`margins-days-${d}`}
              className={`px-2 py-1 font-mono text-[10px] tracking-widest border ${
                days === d ? "border-[var(--tc-red)] text-[var(--tc-red)]" : "border-[#222] text-zinc-400 hover:border-[var(--tc-red)]"
              }`}
            >
              {d}D
            </button>
          ))}
        </div>
      </div>
      {loading || !data ? (
        <div className="py-6 text-center text-zinc-500 font-mono text-xs">LOADING…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Revenue" value={`$${data.revenue.toFixed(0)}`} testid="margins-revenue" />
            <Stat label="Cost (COGS)" value={`$${data.cost.toFixed(0)}`} testid="margins-cost" />
            <Stat label="Profit" value={`$${data.profit.toFixed(0)}`} accent testid="margins-profit" />
            <Stat label="Margin" value={`${data.margin_pct.toFixed(1)}%`} accent testid="margins-pct" />
          </div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-3 flex justify-between items-center flex-wrap gap-2">
            <span>
              {data.order_count} ORDER{data.order_count === 1 ? "" : "S"} IN LAST {data.days} DAYS
              {data.missing_cost_products.length > 0 && (
                <span className="ml-3 text-[var(--tc-red)]">
                  · {data.missing_cost_products.length} PRODUCT{data.missing_cost_products.length === 1 ? "" : "S"} MISSING COST_PER_UNIT
                </span>
              )}
            </span>
            <button
              onClick={() => setShowOrders((s) => !s)}
              className="text-zinc-300 hover:text-[var(--tc-red)] border border-[#222] px-2 py-1 hover:border-[var(--tc-red)]"
              data-testid="margins-toggle-orders"
            >
              {showOrders ? "HIDE PER-ORDER" : "SHOW PER-ORDER (50 LOWEST-MARGIN)"}
            </button>
          </div>
          {data.estimated_cost_count > 0 && (
            <div className="font-mono text-[10px] tracking-widest text-amber-500/80 mt-2" data-testid="margins-estimated-note">
              · {data.estimated_cost_count} READY-MADE PRODUCT{data.estimated_cost_count === 1 ? "" : "S"} USE ESTIMATED COSTS — REFINE IN READY-MADE PRODUCTS TAB FOR EXACT MARGINS
            </div>
          )}
          {data.missing_cost_products.length > 0 && (
            <div className="font-body text-xs text-zinc-400 mt-2" data-testid="margins-missing-list">
              <p>These <strong>sold</strong> products have no <code className="bg-[#0d0d0d] px-1">cost_per_unit</code> yet, so their profit is overstated. Set it in the Custom / Ready-Made Products tab:</p>
              <ul className="mt-1 space-y-0.5">
                {data.missing_cost_products.map((m) => (
                  <li key={m.id} className="text-zinc-300">
                    · {m.name}{m.style ? ` (${m.style})` : ""} — {m.units_sold} sold
                    {!m.in_catalog && <span className="text-zinc-500"> · not in catalog (Etsy/custom)</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {showOrders && data.orders && data.orders.length > 0 && (
            <div className="mt-4 overflow-x-auto border border-[#222]" data-testid="margins-orders-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#222] bg-[#0d0d0d]">
                    <th className="text-left font-mono text-[10px] tracking-widest text-zinc-500 px-3 py-2">ORDER #</th>
                    <th className="text-left font-mono text-[10px] tracking-widest text-zinc-500 px-3 py-2">CUSTOMER</th>
                    <th className="text-right font-mono text-[10px] tracking-widest text-zinc-500 px-3 py-2">REVENUE</th>
                    <th className="text-right font-mono text-[10px] tracking-widest text-zinc-500 px-3 py-2">COST</th>
                    <th className="text-right font-mono text-[10px] tracking-widest text-zinc-500 px-3 py-2">PROFIT</th>
                    <th className="text-right font-mono text-[10px] tracking-widest text-zinc-500 px-3 py-2">MARGIN</th>
                  </tr>
                </thead>
                <tbody>
                  {data.orders.map((o) => (
                    <tr key={o.id} className="border-b border-[#1a1a1a]" data-testid={`margin-order-${o.order_number}`}>
                      <td className="px-3 py-2 font-mono text-xs text-zinc-300">#{o.order_number}</td>
                      <td className="px-3 py-2 text-zinc-300">{o.customer_name || "—"}</td>
                      <td className="px-3 py-2 text-right text-zinc-300">${o.revenue.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right text-zinc-400">${o.cost.toFixed(2)}</td>
                      <td className={`px-3 py-2 text-right ${o.profit < 0 ? "text-red-400" : "text-zinc-300"}`}>${o.profit.toFixed(2)}</td>
                      <td className={`px-3 py-2 text-right font-mono text-xs ${o.margin_pct < 30 ? "text-amber-400" : o.margin_pct < 50 ? "text-zinc-300" : "text-green-400"}`}>{o.margin_pct.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div className="border border-[#222] p-4" data-testid={testid}>
      <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-2">{label.toUpperCase()}</div>
      <div className={`font-display text-3xl ${accent ? "text-[var(--tc-red)]" : ""}`}>{value}</div>
    </div>
  );
}

// ============ SCHEDULED BLASTS QUEUE ============

function ScheduledBlastsPanel({ pw }) {
  const [blasts, setBlasts] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    adminListBlasts(pw)
      .then((r) => setBlasts(r.blasts || []))
      .catch(() => toast.error("Failed to load blast queue"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // Refresh whenever the compose UI schedules something.
    const onScheduled = () => reload();
    window.addEventListener("tc-blast-scheduled", onScheduled);
    // Auto-refresh every 60s so "scheduled → sent" transitions surface.
    const t = setInterval(reload, 60_000);
    return () => {
      window.removeEventListener("tc-blast-scheduled", onScheduled);
      clearInterval(t);
    };
  }, [pw]);  // eslint-disable-line react-hooks/exhaustive-deps

  const cancel = async (id) => {
    if (!window.confirm("Cancel this scheduled blast? It won't be sent.")) return;
    try {
      await adminCancelBlast(id, pw);
      toast.success("Cancelled");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    }
  };

  if (loading) {
    return (
      <section data-testid="blasts-queue-panel">
        <div className="flex items-center gap-3 mb-3">
          <Clock size={20} className="text-[var(--tc-red)]" />
          <h2 className="font-display text-3xl uppercase">Blast Queue</h2>
        </div>
        <div className="py-6 text-center text-zinc-500 font-mono text-xs">LOADING…</div>
      </section>
    );
  }

  if (blasts.length === 0) {
    return (
      <section data-testid="blasts-queue-panel">
        <div className="flex items-center gap-3 mb-3">
          <Clock size={20} className="text-[var(--tc-red)]" />
          <h2 className="font-display text-3xl uppercase">Blast Queue</h2>
          <span className="font-mono text-[10px] tracking-widest text-zinc-500">· EMPTY</span>
        </div>
        <p className="font-mono text-[10px] tracking-widest text-zinc-600">
          USE THE COMPOSE FORM ABOVE TO SCHEDULE A BLAST FOR LATER.
        </p>
      </section>
    );
  }

  return (
    <section data-testid="blasts-queue-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-3">
          <Clock size={20} className="text-[var(--tc-red)]" />
          <h2 className="font-display text-3xl uppercase">Blast Queue</h2>
          <span className="font-mono text-[10px] tracking-widest text-zinc-500">· {blasts.length}</span>
        </div>
        <button onClick={reload} className="p-2 border border-[#222] hover:border-[var(--tc-red)] hover:text-[var(--tc-red)]" aria-label="Refresh blast queue" data-testid="blasts-refresh">
          <RefreshCw size={14} />
        </button>
      </div>
      <div className="border border-[#222] divide-y divide-[#1a1a1a]">
        {blasts.map((b) => (
          <BlastRow key={b.id} blast={b} onCancel={() => cancel(b.id)} />
        ))}
      </div>
    </section>
  );
}

function BlastRow({ blast, onCancel }) {
  const when = blast.scheduled_for ? new Date(blast.scheduled_for) : null;
  const status = blast.status || "scheduled";
  const colors = {
    scheduled: "border-[var(--tc-blue,#3b82f6)] text-[var(--tc-blue,#3b82f6)]",
    processing: "border-yellow-500 text-yellow-500",
    sent: "border-green-600 text-green-500",
    failed: "border-[var(--tc-red)] text-[var(--tc-red)]",
    cancelled: "border-zinc-700 text-zinc-500",
  };
  return (
    <div className="p-3 flex items-center gap-3 flex-wrap" data-testid={`blast-row-${blast.id}`}>
      <span className={`inline-flex px-2 py-0.5 border text-[9px] font-mono tracking-widest uppercase ${colors[status] || colors.scheduled}`}>
        {status}
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-ui font-bold uppercase tracking-wider text-sm truncate">{blast.subject}</div>
        <div className="font-mono text-[10px] text-zinc-500 mt-1">
          {blast.segment_key.toUpperCase()} · {status === "sent" ? `${blast.sent_count}/${blast.intended || "?"} SENT` : `~${blast.estimated_recipients || 0} RECIPIENTS`}
          {when && (<> · {when.toLocaleString()}</>)}
        </div>
      </div>
      {status === "scheduled" && (
        <button
          onClick={onCancel}
          data-testid={`blast-cancel-${blast.id}`}
          className="p-1.5 border border-[#222] hover:border-[var(--tc-red)] hover:text-[var(--tc-red)]"
          title="Cancel"
          aria-label="Cancel scheduled blast"
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}

